import {
  Component,
  Plugins,
  matchExpression,
  Expressions,
} from '@packages/systems/wfdl';
import {EElement} from '@packages/systems/wfdl-ast';
import {qualify} from '@packages/systems/wfdl-Qualified';
import {
  isUserComponent,
  updateComponentRenderInComponentMap,
  isOptimizationExperienceElement,
  isOptimizationVariationElement,
} from '@packages/utilities/platform';
import {pair} from '@packages/utilities/Pair';
import {getMatchingVariationChild} from './getMatchingVariationChild';
import {updateCurrentVariationData} from './updateCurrentVariationData';

type Context = {
  readonly currentVariationId: string | null;
  readonly userComponents: Set<string>;
  readonly experienceAtoms: Set<string>;
  readonly userComponentsOnly?: boolean;
  matchingVariationAtomId?: string | null;
};

const optimizationMatcher = matchExpression({
  Element: (data) => (context: Context) => {
    if (isUserComponent(data.type)) {
      context.userComponents.add(data.type[1]);
      return pair(context, EElement(data));
    }

    if (context.userComponentsOnly) {
      return pair(context, EElement(data));
    }

    if (isOptimizationExperienceElement(data)) {
      context.experienceAtoms.add(data.id);
      const matchingVariationAtomId = getMatchingVariationChild(
        data,
        context.currentVariationId
      )?.id;

      return pair({...context, matchingVariationAtomId}, EElement(data));
    }

    if (isOptimizationVariationElement(data)) {
      return pair(
        context,
        updateCurrentVariationData({
          variationElement: data,
          currentVariationId: context.currentVariationId,
          matchingVariationAtomId: context.matchingVariationAtomId,
        })
      );
    }

    return pair(context, EElement(data));
  },
  _: (expression) => (context: Context) => pair(context, expression),
});

type Params = {
  componentMap: Component.ComponentMap;
  componentName: string;
  currentVariationId: string | null;
  userComponentsOnly?: boolean;
};

export function updateEveryComponentInMap({
  componentMap,
  componentName,
  currentVariationId,
  userComponentsOnly,
}: Params): Component.ComponentMap {
  const componentType = qualify(
    Plugins.name('__SitePlugin'),
    Component.name(componentName)
  );

  const component = componentMap.get(componentType);

  if (!component) {
    return componentMap;
  }

  const userComponents = new Set<string>();
  const experienceAtoms = new Set<string>();
  const componentRender = Component.getRender(component);
  const context = {
    currentVariationId,
    userComponents,
    experienceAtoms,
    userComponentsOnly,
  };

  const nextRender = Expressions.everywhereWithContext(optimizationMatcher)(
    context,
    componentRender
  );

  if (userComponentsOnly && userComponents.size < 1) {
    return componentMap;
  }

  let maybeUpdatedComponentMap = componentMap;

  if (experienceAtoms.size) {
    maybeUpdatedComponentMap = updateComponentRenderInComponentMap(
      maybeUpdatedComponentMap,
      componentType,
      nextRender
    );
  }

  userComponents.forEach((nestedComponentName) => {
    maybeUpdatedComponentMap = updateEveryComponentInMap({
      componentName: nestedComponentName,
      componentMap: maybeUpdatedComponentMap,
      currentVariationId,
    });
  });

  return maybeUpdatedComponentMap;
}
